import { useCallback, useEffect, useRef, useState } from "react"
import {
  ActivityIndicator,
  Keyboard,
  Pressable,
  TextInput,
  TextStyle,
  View,
  ViewStyle,
} from "react-native"

import { Ionicons } from "@expo/vector-icons"
import { router, useLocalSearchParams } from "expo-router"
import { KeyboardAwareScrollView } from "react-native-keyboard-controller"

import { Button, Text, TextField } from "src/components/Ignite"
import { useAuth } from "src/context/AuthContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { logSentryBreadcrumb } from "src/utils/crashReporting"

const MIN_PASSWORD_LENGTH = 6
const RESET_CODE_LENGTH = 6 // determined by the backend (Cognito)
const RESEND_COOLDOWN_TIME = 60 // seconds

export default function ResetPassword() {
  const params = useLocalSearchParams<{ email?: string }>()
  const { authApi, clearAllTemporaryData } = useAuth()
  const { theme, themed } = useAppTheme()
  const [resetCode, setResetCode] = useState(Array(RESET_CODE_LENGTH).fill(""))
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [resetCodeError, setResetCodeError] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [confirmPasswordError, setConfirmPasswordError] = useState("")
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [cooldownTime, setCooldownTime] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Create refs for all reset code inputs
  const inputRefs = useRef<Array<TextInput | null>>(Array(RESET_CODE_LENGTH).fill(null))
  const passwordInputRef = useRef<TextInput | null>(null)

  useEffect(() => {
    if (!params.email) {
      console.error("[Reset Code] No email provided, cannot proceed with password reset")
      router.dismiss()
    }
  }, [params.email])

  useEffect(() => {
    let timer: number | null = null

    if (cooldownTime > 0) {
      timer = setInterval(() => {
        setCooldownTime((time) => Math.max(0, time - 1))
      }, 1000)
    }

    return () => {
      if (timer) {
        clearInterval(timer)
      }
    }
  }, [cooldownTime])

  // Input Change
  // --------------------------------------------------------------------------------------------
  const onPasswordChange = (text: string) => {
    // Clear error if user starts typing again
    if (passwordError) {
      setPasswordError("")
    }
    setPassword(text)
  }

  const onConfirmPasswordChange = (text: string) => {
    // Clear error if user starts typing again
    if (confirmPasswordError) {
      setConfirmPasswordError("")
    }
    setConfirmPassword(text)
  }

  const onResetCodeChange = (text: string, index: number) => {
    // Handle paste events (multiple digits)
    if (text.length > 1) {
      const digits = text.replace(/[^\d]/g, "").split("").slice(0, RESET_CODE_LENGTH)
      const newCode = [...resetCode]

      digits.forEach((digit, i) => {
        if (index + i < RESET_CODE_LENGTH) {
          newCode[index + i] = digit
        }
      })

      setResetCode(newCode)

      // Focus on the next empty field or last field
      const nextIndex = Math.min(index + digits.length, RESET_CODE_LENGTH - 1)
      inputRefs.current[nextIndex]?.focus()

      // If we've filled all fields, advance to the next input
      if (newCode.every((digit) => digit !== "")) {
        passwordInputRef.current?.focus()
      }

      return
    }

    // Handle Single digit input

    // Make sure the input is a digit
    if (text && !/^\d$/.test(text)) return

    const newCode = [...resetCode]
    newCode[index] = text
    setResetCode(newCode)

    // Auto-advance to next field
    if (text && index < RESET_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    // If this was the last digit and all fields are filled, move focus to the password input
    if (index === RESET_CODE_LENGTH - 1 && newCode.every((digit) => digit !== "")) {
      passwordInputRef.current?.focus()
    }
  }

  const handleResetCodeKeyPress = (event: { nativeEvent: { key: string } }, index: number) => {
    // User should be able to backspace to the previous input
    if (event.nativeEvent.key === "Backspace") {
      const newCode = [...resetCode]

      // If current field is empty and we're not at the first input, move back
      if (!resetCode[index] && index > 0) {
        inputRefs.current[index - 1]?.focus()
        newCode[index - 1] = ""
      } else {
        // If current field has a value, just clear it
        newCode[index] = ""
      }

      setResetCode(newCode)
    }
  }

  // Validation
  // --------------------------------------------------------------------------------------------
  const isResetCodeValid = (code: string[]): boolean => {
    return !(code.some((digit) => digit === "") || code.length !== RESET_CODE_LENGTH)
  }

  const isPasswordValid = (): boolean => {
    return password.length >= MIN_PASSWORD_LENGTH
  }

  const isConfirmPasswordValid = (): boolean => {
    return password === confirmPassword
  }

  const validateResetCode = useCallback((code: string[]): boolean => {
    const isValid = isResetCodeValid(code)
    if (isValid) {
      setResetCodeError("")
    } else {
      setResetCodeError(`Please enter the full ${RESET_CODE_LENGTH} digit code`)
    }
    return isValid
  }, [])

  const validatePassword = (): boolean => {
    const isValid = isPasswordValid()
    if (isValid) {
      setPasswordError("")
    } else {
      setPasswordError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long`)
    }
    return isValid
  }

  const validateConfirmPassword = (): boolean => {
    const isValid = isConfirmPasswordValid()
    if (isValid) {
      setConfirmPasswordError("")
    } else {
      setConfirmPasswordError("Passwords must match")
    }
    return isValid
  }

  const runValidations = (): boolean => {
    return validatePassword() && validateConfirmPassword() && validateResetCode(resetCode)
  }

  // Submit
  // --------------------------------------------------------------------------------------------
  const onSubmit = async () => {
    if (!runValidations()) {
      console.error("[onSubmit] Validation failed")
      return
    }

    try {
      const code = resetCode.join("")
      setIsLoading(true)
      setIsError(false)

      const response = await authApi.resetForgottenPassword({
        email: params.email ?? "",
        code,
        newPassword: password,
      })

      if (response.kind !== "ok") {
        throw new Error("Failed to reset password")
      }

      logSentryBreadcrumb("password_reset_success")

      clearAllTemporaryData()

      setIsSuccess(true)
    } catch (error: any) {
      logSentryBreadcrumb("password_reset_error")
      console.log("Password Reset Error", error)
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const canSubmit = (): boolean => {
    if (isLoading) return false

    return isResetCodeValid(resetCode) && isPasswordValid() && isConfirmPasswordValid()
  }

  const onResendCode = async () => {
    if (cooldownTime > 0 || isResending) {
      console.warn("[onResendCode] Cooldown period or resending in progress, cannot resend code")
      return
    }

    try {
      logSentryBreadcrumb("password_reset_attempt")
      setIsError(false)
      setIsResending(true)
      setResetCode(Array(RESET_CODE_LENGTH).fill(""))

      const response = await authApi.forgotPassword({ email: params.email ?? "" })

      if (response.kind !== "ok") {
        throw new Error("Failed to resend code")
      }
      setCooldownTime(RESEND_COOLDOWN_TIME)
    } catch (error: any) {
      console.error("[onResendCode] Failed to resend code", error)
    } finally {
      setIsResending(false)
    }
  }

  useEffect(() => {
    // Run the validation every time one of the code inputs changes
    validateResetCode(resetCode)
  }, [resetCode, validateResetCode])

  return (
    <View style={themed($container)}>
      <View style={themed($header)}>
        <Pressable
          onPress={() => {
            Keyboard.dismiss()
            router.dismiss()
          }}
        >
          <Ionicons name="arrow-back" size={24} color="black" />
        </Pressable>
      </View>

      <KeyboardAwareScrollView>
        <View>
          <Text preset="heading" style={themed($heading)}>
            Password Reset
          </Text>
          <Text preset="subheading" style={themed($subHeading)}>
            We sent a code to{" "}
            <Text preset="subheading" weight="bold" size="sm">
              {params.email}
            </Text>
          </Text>
          <View style={themed($codeInputContainer)}>
            <View style={themed($codeInputRow)}>
              {Array.from({ length: RESET_CODE_LENGTH }).map((_, index) => (
                <TextField
                  key={index}
                  ref={(ref) => {
                    inputRefs.current[index] = ref
                  }}
                  autoFocus={index === 0}
                  maxLength={RESET_CODE_LENGTH}
                  keyboardType="numeric"
                  selectTextOnFocus
                  style={themed($codeInput)}
                  inputWrapperStyle={themed($codeInputWrapper)}
                  value={resetCode[index]}
                  onChangeText={(text) => onResetCodeChange(text, index)}
                  onKeyPress={(e) => handleResetCodeKeyPress(e, index)}
                  status={resetCodeError ? "error" : undefined}
                  returnKeyType={index === RESET_CODE_LENGTH - 1 ? "done" : "next"}
                />
              ))}
            </View>
            {resetCodeError ? (
              <Text style={themed($resetCodeErrorText)}>{resetCodeError}</Text>
            ) : null}
          </View>

          <View style={themed($passwordInputContainer)}>
            <TextField
              ref={passwordInputRef}
              label="New Password"
              value={password}
              onChangeText={onPasswordChange}
              onBlur={() => validatePassword()}
              secureTextEntry={!showPassword}
              autoCapitalize="none"
              status={passwordError ? "error" : undefined}
              style={themed($passwordInput)}
              inputWrapperStyle={themed($passwordInputWrapper)}
              helper={passwordError}
              RightAccessory={() => (
                <Pressable onPress={() => setShowPassword(!showPassword)} style={themed($eyeIcon)}>
                  <Ionicons
                    name={showPassword ? "eye-off-outline" : "eye-outline"}
                    size={24}
                    color={theme.colors.textDim}
                  />
                </Pressable>
              )}
            />

            <TextField
              label="Confirm Password"
              value={confirmPassword}
              onChangeText={onConfirmPasswordChange}
              secureTextEntry={!showConfirmPassword}
              autoCapitalize="none"
              status={confirmPasswordError ? "error" : undefined}
              style={themed($passwordInput)}
              inputWrapperStyle={themed($passwordInputWrapper)}
              helper={confirmPasswordError}
              onBlur={() => validateConfirmPassword()}
              RightAccessory={() => (
                <Pressable
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={themed($eyeIcon)}
                >
                  <Ionicons
                    name={showConfirmPassword ? "eye-off-outline" : "eye-outline"}
                    size={24}
                    color={theme.colors.textDim}
                  />
                </Pressable>
              )}
            />
          </View>

          {isSuccess && (
            <View style={themed($successContainer)}>
              <View style={themed($successRow)}>
                <Ionicons name="checkmark-circle" size={20} color="green" />
                <Text preset="subheading" size="sm" style={themed($successText)}>
                  Successfully reset password!
                </Text>
              </View>
              <Button
                text="Back to Login"
                preset="sidekick"
                onPress={() => router.dismissTo("/unauth/login")}
              />
            </View>
          )}

          {isError && (
            <View style={themed($errorContainer)}>
              <Text preset="subheading" size="sm" style={themed($errorText)}>
                A problem occurred while resetting your password.
              </Text>
              <Button
                text={cooldownTime > 0 ? `Resend code in ${cooldownTime}s` : "Get a new code"}
                preset="sidekick"
                style={themed($errorResendButton)}
                onPress={onResendCode}
                disabled={cooldownTime > 0 || isResending}
              />

              <Text preset="subheading" size="xs" style={themed($supportText)}>
                Still having trouble? Contact us at
              </Text>
              <Text
                preset="subheading"
                size="xs"
                weight="bold"
                style={[themed($supportText), themed($supportEmail)]}
              >
                support@sidekickplatform.com
              </Text>
            </View>
          )}

          {!isSuccess && !isError && (
            <View>
              <Button
                testID="reset-password-button"
                text="Continue"
                preset="sidekick"
                onPress={onSubmit}
                disabled={!canSubmit()}
                onFocus={runValidations}
              />

              {isLoading && (
                <View style={themed($loadingContainer)}>
                  <Text style={themed($loadingText)}>Resetting password...</Text>
                  <ActivityIndicator color={theme.colors.primary} />
                </View>
              )}

              <View style={themed($resendWrapper)}>
                <Text preset="subheading" size="sm" style={themed($resendPromptText)}>
                  Didn&apos;t receive an email?
                </Text>
                <Button
                  text={cooldownTime > 0 ? `Resend in ${cooldownTime}s` : "Click to resend"}
                  style={[themed($resendCodeButton), themed($resendCodeButtonSpacing)]}
                  preset="default"
                  textStyle={[
                    themed($resendCodeText),
                    cooldownTime > 0 && themed($resendCodeTextDisabled),
                  ]}
                  onPress={onResendCode}
                  disabled={cooldownTime > 0 || isResending}
                />
              </View>
            </View>
          )}
        </View>
      </KeyboardAwareScrollView>
    </View>
  )
}

const $container: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  flex: 1,
  backgroundColor: colors.background,
  padding: spacing.md,
})

const $header: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexDirection: "row",
  alignItems: "center",
  paddingVertical: spacing.md,
})

const $heading: ThemedStyle<TextStyle> = ({ spacing }) => ({
  marginBottom: spacing.sm,
  textAlign: "center",
})

const $subHeading: ThemedStyle<TextStyle> = ({ spacing }) => ({
  fontSize: 16,
  textAlign: "center",
  marginBottom: spacing.lg,
})

const $codeInputContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  marginHorizontal: spacing.xs,
  marginBottom: spacing.xxl,
})

const $codeInputRow: ThemedStyle<ViewStyle> = () => ({
  flexDirection: "row",
  justifyContent: "space-between",
  marginBottom: 12,
})

const $codeInput: ThemedStyle<TextStyle> = ({ colors }) => ({
  width: 45,
  textAlign: "center",
  color: colors.inputText,
})

const $codeInputWrapper: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  borderColor: colors.inputBorder,
  borderWidth: 2,
  paddingVertical: spacing.md,
  width: 45,
  borderRadius: spacing.sm,
})

const $resendCodeButton: ThemedStyle<ViewStyle> = () => ({
  minHeight: 0,
  paddingVertical: 0,
  backgroundColor: "transparent",
  borderWidth: 0,
  marginTop: 0,
})

const $resendCodeText: ThemedStyle<TextStyle> = ({ colors }) => ({
  textDecorationLine: "underline",
  color: colors.primary,
  fontSize: 16,
})

const $resendWrapper: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingTop: spacing.xl,
})

const $resendPromptText: ThemedStyle<TextStyle> = () => ({
  textAlign: "center",
})

const $resendCodeButtonSpacing: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  marginTop: spacing.xs,
})

const $resendCodeTextDisabled: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.textDim,
  textDecorationLine: "none",
})

const $passwordInputContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  gap: spacing.lg,
})

const $passwordInputWrapper: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  borderRadius: spacing.sm,
  borderColor: colors.inputBorder,
  borderWidth: 2,
})

const $passwordInput: ThemedStyle<TextStyle> = ({ spacing }) => ({
  marginTop: spacing.sm,
})

const $resetCodeErrorText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.textDim,
  marginTop: 4,
  textAlign: "center",
  fontSize: 14,
})

const $loadingContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  marginTop: spacing.md,
})

const $loadingText: ThemedStyle<TextStyle> = () => ({
  marginBottom: 8,
})

const $successContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  marginTop: spacing.xl,
})

const $successRow: ThemedStyle<ViewStyle> = () => ({
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
})

const $successText: ThemedStyle<TextStyle> = () => ({
  marginBottom: 0,
})

const $errorContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  marginTop: spacing.xl,
})

const $supportText: ThemedStyle<TextStyle> = ({ colors, spacing }) => ({
  color: colors.textDim,
  textAlign: "center",
  marginTop: spacing.xs,
})

const $errorResendButton: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  marginBottom: spacing.md,
  marginTop: spacing.sm,
})

const $errorText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.error,
  marginTop: 4,
  textAlign: "center",
  fontSize: 14,
})

const $supportEmail: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.primary,
})

const $eyeIcon: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  padding: spacing.xs,
})
